import { Component, createMemo, For } from "solid-js";
import { UnifiedItem } from "../../explorer/src/types/data";
import { CivFlag } from "../../explorer/src/components/CivFlag";
import { TimelineChart } from "./Charts";
import { BUILD_ORDER_GROUPING_INTERVAL } from "./config";
import { formatSecondsAsDuration, groupedStepsByPeriod, resourcesToDataSeries } from "./parsing/parseUtils";
import { GameSummary } from "./parsing/types";
import { Icon } from "../../explorer/src/components/Icon";

const BuildStats: Component<{ stats: GameSummary["items"] }> = (props) => {
  const units = createMemo(() => props.stats.filter((s) => s.item.type === "unit"));
  const buildings = createMemo(() => props.stats.filter((s) => s.item.type === "building"));
  return (
    <>
      <div class="bg-item-unit/50 flex flex-wrap p-2 gap-2 rounded mb-4">
        <For each={units()}>
          {(unit) => (
            <div class="flex items-center flex-col">
              <img src={unit.item.icon} class="w-8 h-8 object-contain" title={unit.item.name} />
              <span class="text-xs">
                {unit.counts.build}
                {unit.counts.lost > 0 && <span class="ml-0.5 opacity-50">-{unit.counts.lost}</span>}
              </span>
            </div>
          )}
        </For>
      </div>
      <div class="bg-item-building/50 flex flex-wrap p-2 rounded mb-4">
        <For each={buildings()}>
          {(building) => (
            <div class="flex items-center flex-col">
              <img src={building.item.icon} class="w-8 h-8 object-contain" title={building.item.name} />
              <span class="text-xs">
                {building.counts.build}
                {building.counts.lost > 0 && <span class="ml-0.5 opacity-50">-{building.counts.lost}</span>}
              </span>{" "}
            </div>
          )}
        </For>
      </div>
    </>
  );
};

const BuildAction: Component<{ item: UnifiedItem; count?: number; timestamp: number }> = (props) => {
  return (
    <div class="flex p-1 rounded items-center mb-0.5 bg-opacity-50" className={`bg-item-${props.item.type}`}>
      <span class="w-10 text-right text-sm pr-2 opacity-70">{formatSecondsAsDuration(props.timestamp)}</span>
      <div class="flex-none self-start rounded w-8 h-8 p-0 mr-2" className={`bg-item-${props.item.type}`}>
        <img src={props.item.icon} />
      </div>
      <span class="font-bold text-sm flex-auto">{props.item.type=='upgrade' ? props.item.description.split('to').pop().replace('.', '') : props.item.name}</span>
      {props.count > 1 && <span class="text-base mx-2">&times; {props.count}</span>}
    </div>
  );
};

const WrappedIf: Component<{ wrap: boolean }> = (props) => {
  return <>{props.wrap ? <div class="flex flex-col">{props.children}</div> : props.children}</>;
};

export const BuildOrder: Component<{ summaries: GameSummary[]; wrap?: boolean; group?: boolean }> = (props) => {
  const interval = BUILD_ORDER_GROUPING_INTERVAL;
  const maxResourcesAxis = createMemo(() => Math.max(...(props.summaries?.flatMap(({ resources: r }) => [r.food, r.wood, r.gold, r.stone])?.flat() ?? [])));

  return (
    <div class="overflow-auto relative pb-4" style="max-height: calc(100vh - 200px)">
      <div class="grid grid-flow-row-dense gap-y-2 gap-x-4 mx-4" style={{ "grid-template-columns": `repeat(${props.summaries?.length}, 350px)` }}>
        <For each={props.summaries}>
          {(summary, x) => {
            return (
              <>
                <div class="max-w-sm top-0 z-10 pt-4 pb-2 sticky bg-gray-700 flex items-center whitespace-nowrap" style={{ "grid-column": x() + 1, "grid-row": 1 }}>
                  <span style={{ background: summary.color }} class="inline-block flex-none rounded-full w-2 h-2 mr-2"></span>
                  <h1 class="text-lg font-bold truncate">{summary.name}</h1>
                  {props.summaries.length > 2 && <span class="text-gray-400 text-sm ml-auto">{summary.teamName}</span>}
                  <CivFlag abbr={summary.civ.abbr} class="w-10 ml-2" />
                </div>
                <div class="max-w-sm" style={{ "grid-column": x() + 1, "grid-row": 2 }}>
                  <TimelineChart
                    data={resourcesToDataSeries(summary.resources)}
                    timestamps={summary.resources.timestamps}
                    title="Resources"
                    options={{ xGridLines: true, yGridLines: false, maxY: maxResourcesAxis() }}
                  />

                  <BuildStats stats={summary.items} />
                </div>
                <WrappedIf wrap={props.wrap}>
                  <For each={props.group ? groupedStepsByPeriod(summary.groupedSteps, interval) : groupedStepsByPeriod(summary.steps, interval)}>
                    {(period, y) => (
                      <div class="" style={{ "grid-column": x() + 1, "grid-row": y() + 3 }}>
                        {!props.wrap && !(y() % (60 / interval)) && (
                          <h2 class="text-sm font-bold mt-4 text-gray-300 mb-3">{formatSecondsAsDuration(y() * interval)}</h2>
                        )}
                        <For each={period}>{(step) => <BuildAction item={step.item} count={step.count} timestamp={step.timestamp} />}</For>
                      </div>
                    )}
                  </For>
                </WrappedIf>
              </>
            );
          }}
        </For>
      </div>
    </div>
  );
};
